import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, EMPTY, forkJoin, of, ReplaySubject, Subject, Subscription, throwError, timer } from 'rxjs';
import { catchError, concatMap, expand, finalize, take, takeUntil, takeWhile } from 'rxjs/operators';
import { PoBreadcrumb, PoComboComponent, PoModalAction, PoModalComponent, PoPageAction } from '@po-ui/ng-components';

import { NotificationServiceHM } from 'src/app/menu/notification-item/notification-item.service';
import { MainService } from 'src/app/app.service';
import { DiffExportResponse, ReviewFilesMeta, ReviewService } from '../../review.service';
import { commitResourceLabel, parseCommitResourceId, parsePullResourceId, pullResourceLabel } from '../../pull-resource-id.util';
import { sourceProviderLabel } from '../../../core/utils/provider-label.util';
import { resourceTypeLabel } from '../../../core/utils/resource-type-label.util';
import { JobsService } from '../../../core/api/jobs.service';
import { TfvcService } from '../../../core/api/tfvc.service';
import { GitService } from '../../../core/api/git.service';
import { SmartReviewPermissionsService } from '../../../core/services/smart-review-permissions.service';
import { reviewOpenBlock } from '../../utils/review-open.util';
import { ReviewFile } from '../../interfaces/review-file.interface';
import { ReviewAnswer, ReviewAnswerItem, ReviewAnswersResponse } from '../../interfaces/review-answer.interface';
import { ReviewFeedback } from '../../interfaces/review-feedback.interface';
import { ReviewFeedbackChange } from '../review-feedback/review-feedback.component';
import { Review } from '../../interfaces/review.interface';
import { SmartReviewJob } from '../../../core/interfaces/job.interface';
import { DiffPair, parseDiffToPair, detectLanguage, extractFileDiff } from '../../utils/diff-parser.util';
import {
  PromptGenerationRequest,
  SmartReviewAssistantJobResponse,
  TechnicalDocAnswer
} from '../../interfaces/technical-doc.interface';
import { formatTechnicalDocAsText } from '../../utils/technical-doc-formatter.util';
import { TERMINAL_STATUSES as TERMINAL_JOB_STATUSES, FAILED_STATUSES as FAILED_JOB_STATUSES } from '../../review-constants';
import { CHECKING_FRESHNESS, ChangesetFreshness, buildChangesetFreshness, buildRevisionFreshness } from '../../utils/changeset-freshness.util';
import { ReviewMonacoViewerComponent } from '../review-monaco-viewer/review-monaco-viewer.component';
import { buildReviewDeleteKey, getDeleteReviewErrorMessage } from '../../utils/review-delete.util';
import { getApiErrorMessage, getBackendErrorCode, getHttpStatus, isServiceUnavailable, showServiceUnavailableToast } from '../../../core/utils/http-error-message.util';
const MAX_TECH_DOC_POLL_ATTEMPTS = 10;

/** Motivo local (não vem da API) de o conteúdo estar fechado: review com erro. */
const REVIEW_FAILED_CODE = 'review_failed';

const FAILED_REVIEW_STATUSES = ['failed', 'dead'];

@Component({
  selector: 'app-review-detail',
  templateUrl: './review-detail.component.html',
  styleUrls: ['./review-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReviewDetailComponent implements OnInit, AfterViewInit, OnDestroy, DoCheck {
  @Input() modalMode = false;
  /** Quando true, renderiza apenas o visualizador de código (sem meta/abas). */
  @Input() sourceOnly = false;

  @ViewChild('monacoViewer') private monacoViewerRef?: ReviewMonacoViewerComponent;
  @ViewChild('fileCombo') private fileCombo?: PoComboComponent;
  @ViewChild('deleteReviewModal') deleteReviewModal?: PoModalComponent;
  @ViewChild('overallFeedbackModal') overallFeedbackModal?: PoModalComponent;
  loading = false;
  /**
   * A API recusou abrir este review para quem está logado.
   *
   * Os motivos são da API: o provedor git não dá acesso ao repositório, falta TFVC, ou o
   * review é de outra pessoa e a área não o compartilha. A visão global (admin, audit)
   * mostra que o review existe, mas o conteúdo segue a origem. A API responde 403 em
   * arquivos, respostas e diff; sem esta marca a tela abria o viewer vazio, como se o
   * review não tivesse alteração nenhuma.
   */
  reviewAccessDenied = false;
  /** Mensagem da API para o acesso negado: só ela sabe o motivo e o que fazer. */
  reviewAccessDeniedMessage = '';
  /** Código do motivo que a API mandou no 403 (repository_access_denied, ...). */
  reviewAccessDeniedCode = '';
  deleteReviewLoading = false;
  resourceType = '';
  resourceId = '';
  importedMode = false;
  private deleteInFlightKey: string | null = null;

  readonly scoreTooltip =
    'Nota de 1 a 10 para o risco da alteração.\n\n' +
    'O problema mais grave define a faixa: duas ou mais altas ficam entre 1 e 3, uma alta ' +
    'fica em 4, média entre 5 e 7, baixa entre 7 e 8. Os demais problemas descontam dentro ' +
    'da faixa.\n\n' +
    '10 = nenhum problema.';

  readonly statusTooltip =
    'Resultado da revisão:\n\n' +
    'Aprovado: sem problemas.\n' +
    'Ajustes Necessários: problemas a corrigir, com no máximo um de severidade alta.\n' +
    'Rejeitado: dois ou mais problemas de severidade alta.\n' +
    'Insuficiente: a revisão não pôde ser concluída.';

  getSummaryStatusType(status?: string): string {
    const s = (status || '').toLowerCase();
    if (s.includes('aprovado')) { return 'success'; }
    if (s.includes('ajuste')) { return 'warning'; }
    if (s.includes('rejeitado')) { return 'danger'; }
    return 'info';
  }


  deleteReviewPrimaryAction: PoModalAction = {
    label: 'Excluir',
    danger: true,
    action: () => this.confirmDeleteReview(),
    disabled: false
  };
  deleteReviewSecondaryAction: PoModalAction = {
    label: 'Cancelar',
    action: () => this.closeDeleteReviewModal()
  };
  overallFeedbackModalCloseAction: PoModalAction = {
    label: 'Fechar',
    action: () => this.closeOverallFeedbackModal()
  };

  // ─── Standalone source view (igual VS Code: abre fonte direto, sem diff) ──────────────────
  standaloneSource = '';
  sourceLanguage = 'plaintext';

  breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Reviews', link: '/menu/smart-review/reviews' },
      { label: '...' }
    ]
  };
  /**
   * Ações do cabeçalho, em ordem.
   *
   * É CAMPO, não getter. O po-page-default passa `visibleActions.slice(2)` ao po-dropdown,
   * e um getter devolveria array novo com closures novas a cada ciclo de detecção: o
   * popup se reconstruía sob o cursor e o clique não chegava a lugar nenhum. Os dois
   * primeiros, renderizados como botão, funcionavam — só as ações do menu falhavam, em
   * silêncio. Mesmo erro que a tabela de Pull Requests já tinha ensinado.
   *
   * A visibilidade é resolvida AQUI, não pelo PO. O po-page-default monta a lista com
   * `actions.filter(a => a.visible !== false)`, e uma função nunca é `=== false`: passar
   * `visible` como função deixava as cinco ações sempre visíveis, e clicar em uma que
   * não se aplica não fazia nada — as guardas dos handlers retornam cedo, em silêncio.
   * `disabled` continua podendo ser função, essa o PO avalia (`actionIsDisabled`).
   *
   * O array só é recriado quando o CONJUNTO visível muda. A identidade importa: o PO
   * entrega uma fatia dele ao menu "Outras ações", e recriá-lo a cada ciclo reconstrói
   * o popup sob o cursor, e o clique nos itens não chega a lugar nenhum.
   *
   * São ações do REVIEW, e é por isso que ficam juntas: classificar, publicar e excluir
   * operam sobre o review inteiro, não sobre o arquivo em foco.
   *
   * O po-page-default renderiza três como botão e recolhe o excedente num menu. Num Pull
   * Request são quatro, e quem recolhe é a exclusão — de propósito: é a única
   * destrutiva, é a menos usada, e tirá-la do alcance do clique distraído é ganho, não
   * perda. Num changeset são três e nada é recolhido, então a tela do TFVC continua
   * exatamente como era.
   *
   * `show` é predicado nosso, e não o `visible` do PoPageAction, justamente para não
   * parecer que o PO o avalia.
   */
  private readonly allPageActions: Array<PoPageAction & { show: () => boolean }> = [
    { label: 'Voltar', icon: 'po-icon-arrow-left', action: () => this.goBack(), show: () => true },
    {
      // O rótulo muda com o estado: "Classificar review" vira "Editar avaliação" depois
      // da primeira classificação. Como PoPageAction.label é string e não função, quem
      // atualiza é syncPageActions — mutando ESTE objeto, que o PO relê a cada render.
      label: 'Classificar review',
      icon: 'po-icon-edit',
      action: () => this.openOverallFeedbackModal(),
      show: () => this.canShowOverallFeedbackQuickAction
    },
    {
      label: 'Publicar achados no PR',
      icon: 'po-icon-share',
      action: () => this.openPublishCommentsModal(),
      disabled: () => this.publishingComments,
      show: () => this.canPublishComments
    },
    {
      // Aparece só quando o review não tem análise. É a saída desse estado, e não havia
      // nenhuma: reprocessar re-executa o review com o payload original, que não pedia o
      // assistente — o review voltava ao mesmo lugar, e a tela mandava tentar de novo.
      label: 'Gerar análise',
      icon: 'po-icon-star',
      action: () => this.runCodeReviewAssistant(),
      disabled: () => this.runningAssistant,
      show: () => this.canRunCodeReviewAssistant
    },
    {
      label: 'Excluir review',
      icon: 'po-icon-delete',
      action: () => this.openDeleteReviewModal(),
      show: () => this.canDeleteReview
    }
  ];

  /**
   * O que o cabeçalho renderiza. Preenchido por `syncPageActions`, que roda no ngDoCheck.
   *
   * Começa vazio de propósito: os predicados leem campos declarados abaixo, e avaliá-los
   * num inicializador de campo os leria como `undefined`.
   */
  pageActions: PoPageAction[] = [];

  /** Assinatura do último conjunto visível, para só recriar o array quando ele muda. */
  private shownPageActions = '';

  /** Em voo enquanto o assistente é enfileirado, para não disparar duas vezes. */
  runningAssistant = false;

  files: ReviewFile[] = [];
  selectedFile: ReviewFile | null = null;
  fileOptions: { label: string; value: string }[] = [];
  selectedFileValue = '';
  private fileByOptionValue = new Map<string, ReviewFile>();

  /** Arquivos que o combo e a navegação percorrem: todos, ou só os com ações ou sugestões. */
  visibleFiles: ReviewFile[] = [];
  /** Num PR de centenas de arquivos, a maioria é só formatação; o filtro deixa o resto. */
  onlyFilesWithFindings = false;
  filesWithFindingsCount = 0;

  /** As respostas chegam depois dos arquivos; enquanto isso a análise está "carregando", não "ausente". */
  answersLoading = false;
  answersLoadError = false;

  /**
   * Resposta de cada arquivo, resolvida uma vez quando as respostas chegam. O casamento
   * por pontuação custa arquivos × respostas, e o combo pergunta por todos os arquivos.
   */
  private answerItemByFile = new Map<ReviewFile, ReviewAnswerItem | null>();

  /** Descarta respostas de um carregamento anterior (troca de rota na mesma instância). */
  private loadSeq = 0;

  answerItems: ReviewAnswerItem[] = [];
  answer: ReviewAnswer | null = null;

  /**
   * Explica a ausência de achados no lugar onde eles apareceriam.
   *
   * "Ainda não disponível" não dizia o que houve nem o que fazer: um review processado sem
   * o assistente tem diff e prompt, nenhum achado, e ficava indistinguível de um defeito de
   * tela. Fica vazio enquanto carrega, para não afirmar ausência antes de saber.
   *
   * E fica vazio no modal e no modo somente-fonte: a mensagem manda usar "Gerar análise",
   * em Outras ações, e essas ações de cabeçalho não existem fora da tela de detalhe.
   */
  get emptyAnalysisMessage(): string {
    if (this.loading || this.modalMode || this.sourceOnly) { return ''; }
    // A versão de runtime (.nvmrc) tem diff, mas o backend nunca a envia ao modelo: ela só
    // serve de referência para a análise dos outros arquivos de build. Sem este caso, num PR
    // só com ela, a tela mandava usar "Gerar análise" — que não produziria nada.
    if (this.selectedFile?.contextOnly) {
      return 'Este arquivo entra só como referência de versão para a análise dos outros arquivos '
        + 'de build e não é enviado para análise.';
    }
    // O diff ignora espaço: reindentação e troca de fim de linha chegam sem nenhuma linha
    // adicionada ou removida, e o backend não envia esses arquivos ao modelo. Sem esta
    // mensagem a tela caía no texto de "análise não pedida" e mandava gerar uma análise
    // que nunca vai existir para este arquivo.
    if (this.diffHasNoLineChanges) {
      return 'Este arquivo não tem alteração de código — só espaços, indentação ou fim de linha — '
        + 'e por isso não foi enviado para análise.';
    }
    if (this.answersLoading) {
      return 'Carregando a análise…';
    }
    if (this.answersLoadError) {
      return 'Não foi possível carregar a análise deste review. Recarregue a página para tentar de novo.';
    }
    if (this.answerItems.length) {
      return 'Não há análise para este arquivo.';
    }
    return 'Este review ainda não passou pelo assistente, então não há achados a exibir. '
      + 'Use "Gerar análise", em Outras ações, para pedi-la.';
  }

  /** O diff do arquivo aberto carregou e não tem linha adicionada nem removida. */
  diffHasNoLineChanges = false;
  selectedAnswerItem: ReviewAnswerItem | null = null;

  /** O feedback do próprio usuário por achado: é o que o card deixa alterar. */
  feedbackByFindingId = new Map<string, ReviewFeedback>();
  overallFeedback: ReviewFeedback | null = null;
  /**
   * O de outros usuários da área (issue e Pull Request), só para leitura. Separado do próprio
   * porque a lista traz os dois, e um mapa só por achado deixava o do outro sobrescrever o
   * seu — aparecendo no componente editável como se fosse seu.
   */
  otherFeedbackByFindingId = new Map<string, ReviewFeedback[]>();
  otherOverallFeedback: ReviewFeedback[] = [];

  get selectedAnswerArtifactId(): number | undefined {
    return this.selectedAnswerItem?.answerId;
  }

  analysisMode: 'code_review' | 'technical_doc' = 'code_review';
  technicalDocLoading = false;
  technicalDocActionLoading = false;
  technicalDocAvailable = false;
  technicalDocAnswerItems: ReviewAnswerItem<TechnicalDocAnswer>[] = [];
  technicalDocAnswer: TechnicalDocAnswer | null = null;
  technicalDocJob: SmartReviewJob | null = null;
  technicalDocStatusMessage = '';
  private technicalDocPollingJobId: string | null = null;
  private technicalDocChecked = false;

  diffPair: DiffPair = { original: '', modified: '', language: 'plaintext', lineCount: 0, stats: { added: 0, removed: 0 }, hunks: [], isAddedFile: false };

  /** True quando o review é do tipo standalone — exibe fonte ao invés de diff (igual VS Code). */
  get isStandalone(): boolean {
    return (this.resourceType ?? '').toLowerCase() === 'standalone';
  }

  /**
   * True quando o viewer deve renderizar em modo single-editor (sem painel de diff):
   * review standalone OU arquivo novo (added) dentro de um diff/changeset.
   */
  get viewerStandaloneMode(): boolean {
    return this.isStandalone || this.diffPair.isAddedFile;
  }

  get viewerStandaloneSource(): string {
    if (this.isStandalone) { return this.standaloneSource; }
    if (this.diffPair.isAddedFile) { return this.diffPair.modified; }
    return this.standaloneSource;
  }

  get viewerSourceLanguage(): string {
    if (this.isStandalone) { return this.sourceLanguage; }
    if (this.diffPair.isAddedFile) { return this.diffPair.language; }
    return this.sourceLanguage;
  }

  /** True quando o review é do tipo comparison. */
  get isComparison(): boolean {
    return (this.resourceType ?? '').toLowerCase() === 'comparison';
  }

  /** True quando o review é do tipo changeset. */
  get isChangeset(): boolean {
    return (this.resourceType ?? '').toLowerCase() === 'changeset';
  }

  /** True quando o review é do tipo issue. */
  get isIssue(): boolean {
    return (this.resourceType ?? '').toLowerCase() === 'issue';
  }

  /**
   * Como o tipo deste review se chama na tela.
   *
   * O `resourceType` cru é identificador interno: a tela mostrava `pullrequest` e passou
   * a mostrar `pull` quando ele foi renomeado. O rótulo é o mesmo da listagem, de
   * propósito — é o mesmo review visto de dois lugares.
   */
  get resourceTypeLabel(): string {
    return resourceTypeLabel(this.resourceType);
  }

  /** True quando o review é de repositório git: Pull Request ou commit. */
  get isGitChange(): boolean {
    const type = (this.resourceType ?? '').toLowerCase();
    return type === 'pull' || type === 'commit';
  }

  /** True quando o review é a comparação de um arquivo contra revisão de repositório git. */
  get isGitComparison(): boolean {
    return (this.resourceType ?? '').toLowerCase() === 'git-comparison';
  }

  /**
   * Como a versão se chama nesta origem.
   *
   * Em repositório git o valor é um SHA, e chamá-lo de changeset afirma o que ele não é —
   * o cabeçalho mostrava "Changeset: 562f5fa → 50da521", dois commits sob o rótulo de uma
   * numeração que só existe no TFVC.
   */
  get reviewVersionTitle(): string {
    if (this.isPullRequest) { return 'Pull Request'; }
    return this.isGitChange || this.isGitComparison ? 'Commit' : 'Changeset';
  }

  /** True quando o review é de um Pull Request. */
  get isPullRequest(): boolean {
    return (this.resourceType ?? '').toLowerCase() === 'pull';
  }

  /**
   * Como o review de repositório git se chama na tela.
   *
   * Mesma regra da issue: o recurso tem VÁRIOS arquivos, então o nome dele é a própria
   * identidade — `protheus #42`, `protheus 65b0c5a` —, e não o nome de um dos fontes. A
   * API deixa sourceName e sourceDisplayName vazios exatamente por isso; sem tratar o
   * caso aqui, o título caía no arquivo em foco e o review inteiro passava a se chamar
   * como um arquivo qualquer dele, que muda quando se troca de aba.
   */
  get gitChangeLabel(): string {
    const type = (this.resourceType ?? '').toLowerCase();
    const id = this.resourceId || '';
    if (!id) { return ''; }
    return type === 'commit' ? commitResourceLabel(id) : pullResourceLabel(id);
  }

  private normalizeChangesetLabel(value: string): string {
    return (value || '').trim().replace(/^cs\s+/i, '');
  }

  /** Formato esperado: "arquivo.prw | cs 556251 → 588340" */
  private parseComparisonDisplayName(): { sourceName: string; changesetLabel: string } {
    const display = (this.sourceDisplayName || '').trim();
    if (!display) {
      return { sourceName: '', changesetLabel: '' };
    }

    const pipeIndex = display.indexOf('|');
    if (pipeIndex >= 0) {
      const sourceName = display.slice(0, pipeIndex).trim();
      const rawChangeset = display.slice(pipeIndex + 1).trim();
      return {
        sourceName,
        changesetLabel: this.normalizeChangesetLabel(rawChangeset)
      };
    }
    return { sourceName: display, changesetLabel: '' };
  }

  private get sourceNameOnly(): string {
    const sourceName = (this.reviewSourceName || '').trim();
    if (sourceName) {
      return sourceName;
    }

    return this.parseComparisonDisplayName().sourceName;
  }

  get pageTitle(): string {
    if (this.isIssue) {
      return this.resourceId || this.reviewSourceName || 'Review';
    }
    if (this.isGitChange) {
      return this.gitChangeLabel || 'Review';
    }
    if (this.isChangeset) {
      return this.reviewChangesetNumber || this.resourceId || 'Review';
    }
    if (this.isComparison) {
      return this.sourceNameOnly || this.resourceId || 'Review';
    }
    return this.sourceNameOnly || this.resourceId || 'Review';
  }

  get breadcrumbLabel(): string {
    if (this.isIssue) {
      return this.resourceId || this.sourceDisplayName || this.reviewSourceName || 'Review';
    }
    if (this.isGitChange) {
      return this.gitChangeLabel || 'Review';
    }
    if (this.isChangeset) {
      return this.reviewChangesetNumber || this.resourceId || 'Review';
    }
    if (this.isComparison) {
      return this.sourceNameOnly || this.resourceId || 'Review';
    }
    return this.sourceDisplayName || this.resourceId;
  }

  get isCodeReviewMode(): boolean {
    return this.analysisMode === 'code_review';
  }

  get isTechnicalDocMode(): boolean {
    return this.analysisMode === 'technical_doc';
  }

  publishingComments = false;
  /** Resultado da última publicação, exibido ao lado do botão. */
  publishCommentsSummary = '';

  @ViewChild('publishCommentsModal') publishCommentsModal?: PoModalComponent;

  readonly publishCommentsPrimaryAction: PoModalAction = {
    label: 'Publicar',
    action: () => this.confirmPublishComments()
  };

  readonly publishCommentsSecondaryAction: PoModalAction = {
    label: 'Cancelar',
    action: () => this.publishCommentsModal?.close()
  };

  /**
   * A publicação escreve no diff do Pull Request, que outras pessoas acompanham. Por isso
   * é ação explícita e confirmada, e não um efeito colateral de pedir o review: quem
   * clica aqui já leu os achados e está decidindo que eles merecem ir para o PR.
   *
   * Só Pull Request: um commit já está no histórico e não tem onde receber comentário.
   */
  /**
   * Situação do Pull Request no provedor, recebida junto da lista de arquivos.
   *
   * Vazia quando o recurso não é um PR ou quando o provedor não reportou — e vazia não
   * bloqueia nada, senão todo provedor que omite o estado perderia a publicação.
   */
  pullState = '';

  /** True quando o Pull Request já foi mergeado ou fechado. */
  get isPullClosed(): boolean {
    const state = (this.pullState || '').trim().toLowerCase();
    return state !== '' && state !== 'open';
  }

  get canPublishComments(): boolean {
    // Sem isCodeReviewMode: o botão vive no bloco do review, que é o mesmo nas duas
    // abas. Amarrá-lo à aba faria a ação sumir ao olhar o Entendimento Técnico, e o que
    // ela publica não muda com a aba.
    //
    // A resposta do arquivo em foco serve de sinal de que existe review apurado; o que
    // se publica são os achados de TODOS os fontes.
    return !this.modalMode
      && !this.sourceOnly
      && this.resourceType === 'pull'
      // Pull Request fechado não recebe comentário: num mergeado o achado chega sobre
      // código que já está em produção, num fechado sem merge a discussão acabou. A rota
      // recusa de qualquer forma — aqui a ação some, em vez de aparecer para dar erro.
      && !this.isPullClosed
      && !!this.answer
      && !this.loading
      && !!this.resourceId;
  }

  /**
   * O po-page-content nasce com opacity 0 e só a troca para 1 num setTimeout do próprio
   * ngAfterViewInit. Sendo este componente OnPush, esse tick não chega até ele, e a página
   * ficava em branco até o forkJoin do carregamento chamar markForCheck — segundos num
   * PR com centenas de arquivos. Este setTimeout roda depois do dele e libera o conteúdo.
   */
  ngAfterViewInit(): void {
    setTimeout(() => this.cdr.markForCheck());
  }

  /**
   * Mantém rótulo e conjunto visível das ações em dia com o estado.
   *
   * Roda no ngDoCheck, e não em cada ponto que altera a classificação ou o review: os dois
   * dependem de estado que muda em vários lugares, e amarrá-los a uma lista de chamadas
   * deixaria o próximo caminho novo desatualizado, em silêncio. O custo é uma comparação
   * de string por ciclo.
   *
   * O rótulo muta o objeto da ação; o array só é recriado quando o conjunto visível muda,
   * porque a identidade dele é o que mantém o menu "Outras ações" funcionando.
   */
  ngDoCheck(): void {
    const classifyAction = this.allPageActions[1];
    const label = this.overallFeedbackQuickActionLabel;
    if (classifyAction.label !== label) {
      classifyAction.label = label;
    }

    const shown = this.allPageActions.map(action => (action.show() ? '1' : '0')).join('');
    if (shown !== this.shownPageActions) {
      this.shownPageActions = shown;
      this.pageActions = this.allPageActions.filter(action => action.show());
    }
  }

  /**
   * `slug #número` do PR que receberá os comentários. O modal nomeia o alvo porque a
   * escrita é num repositório que outras pessoas acompanham.
   */
  get publishCommentsTarget(): string {
    const ref = parsePullResourceId(this.resourceId);
    return ref ? `${ref.slug} #${ref.number}` : '';
  }

  openPublishCommentsModal(): void {
    if (!this.canPublishComments || this.publishingComments) { return; }
    this.publishCommentsModal?.open();
  }

  private confirmPublishComments(): void {
    if (this.publishingComments) { return; }
    this.publishingComments = true;
    this.publishCommentsSummary = '';
    this.cdr.markForCheck();

    this.smartReviewService.publishPullRequestComments(this.resourceId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.publishingComments = false;
          this.publishCommentsModal?.close();
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: result => {
          if (result?.unsupported) {
            this.notificationServiceHM.showToast('Este provedor não publica comentário no diff.', 'warning');
            return;
          }
          const posted = result?.posted ?? 0;
          const skipped = result?.skipped ?? 0;
          // Publicar de novo devolve tudo como skipped: é a idempotência funcionando, e
          // dizer "0 publicados" sem explicar pareceria falha.
          this.publishCommentsSummary = posted > 0
            ? `${posted} ${posted === 1 ? 'comentário publicado' : 'comentários publicados'}`
            : `Nada novo a publicar — ${skipped} já ${skipped === 1 ? 'estava' : 'estavam'} no PR`;
          const resolved = result?.resolved ?? 0;
          if (resolved > 0) {
            // Achado que sumiu da resposta teve a discussão encerrada no PR. Sem isto o
            // dev veria comentários sumirem do PR sem a tela dizer por quê.
            this.publishCommentsSummary += ` · ${resolved} ${resolved === 1 ? 'resolvido' : 'resolvidos'}`;
          }
          if ((result?.outOfDiff ?? 0) > 0) {
            // Achado ancorado fora do diff não vira comentário. É informação de
            // qualidade do review, não erro da publicação.
            this.publishCommentsSummary += ` · ${result.outOfDiff} fora do diff`;
          }
          // Nada novo a publicar é sucesso, não aviso: significa que o PR já está com
          // os achados. Marcar como warning faria a idempotência parecer problema.
          this.notificationServiceHM.showToast(this.publishCommentsSummary, 'success');
        },
        error: error => {
          if (isServiceUnavailable(error)) {
            showServiceUnavailableToast(this.notificationServiceHM, error);
            return;
          }
          this.notificationServiceHM.showToast(
            getApiErrorMessage(error, 'Não foi possível publicar os comentários no PR.'), 'error');
        }
      });
  }

  /**
   * Diz se a Visão Geral tem algo a mostrar além das etiquetas.
   *
   * Numa mudança trivial o modelo devolve `understanding` vazio e nenhum ponto positivo
   * — acontece, e é resposta legítima. Sem isto o corpo do card saía em branco.
   */
  get hasOverviewDetail(): boolean {
    return !!this.answer?.understanding?.trim() || !!this.answer?.positives?.length;
  }

  get canShowOverallFeedbackQuickAction(): boolean {
    return !this.modalMode
      && this.isCodeReviewMode
      && !!this.answer
      && !this.loading
      && !!this.resourceType
      && !!this.resourceId;
  }

  get overallFeedbackQuickActionLabel(): string {
    return this.overallFeedback?.id ? 'Editar avaliação' : 'Classificar review';
  }

  get overallFeedbackQuickActionKind(): 'primary' | 'secondary' {
    return this.overallFeedback?.id ? 'secondary' : 'primary';
  }

  private getReviewLifecycleStatusForDelete(): string {
    const derived = this.normalizeStatus(this.reviewStatusDerived);
    if (derived) { return derived; }
    return this.normalizeStatus(this.reviewJobStatus);
  }

  private isReviewTerminalForDelete(): boolean {
    const status = this.getReviewLifecycleStatusForDelete();
    return !!status && this.isTerminalJobStatus(status);
  }

  get canDeleteReview(): boolean {
    return !this.reviewAccessDenied && !!this.resourceType && !!this.resourceId && !this.deleteReviewLoading && this.isReviewTerminalForDelete();
  }

  /**
   * Se cabe oferecer a geração da análise: o review carregou, produziu prompt e não tem
   * resposta. Enquanto carrega fica escondida, para não oferecer a partir de uma ausência
   * que ainda não se sabe se é real.
   *
   * A ausência é do REVIEW, não do arquivo aberto: arquivo sem linha alterada não tem
   * resposta própria num review já analisado, e oferecer a ação ali rodava o assistente
   * sobre o review inteiro de novo.
   */
  get canRunCodeReviewAssistant(): boolean {
    return !this.reviewAccessDenied && !!this.resourceType && !!this.resourceId && !this.loading
      && !this.answer && !this.answerItems.length && this.analysisMode === 'code_review';
  }

  /**
   * Enfileira o assistente de code review.
   *
   * É a única saída do estado "análise pendente": o reprocessamento reexecuta o review com
   * o payload que ele já tinha, e um review pedido sem assistente não passa a ter um por
   * ser reprocessado — voltava ao mesmo lugar, sem dizer por quê.
   */
  runCodeReviewAssistant(): void {
    if (!this.canRunCodeReviewAssistant || this.runningAssistant) { return; }
    this.runningAssistant = true;
    this.cdr.markForCheck();

    this.smartReviewService.runAssistant(this.resourceType, this.resourceId, 'code_review', true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.runningAssistant = false;
          this.notificationServiceHM.showToast(
            'Análise solicitada. Ela aparece aqui assim que o assistente terminar.', 'success');
          this.cdr.markForCheck();
        },
        error: () => {
          this.runningAssistant = false;
          this.notificationServiceHM.showToast('Não foi possível solicitar a análise.', 'error');
          this.cdr.markForCheck();
        }
      });
  }

  get isSharedReviewContext(): boolean {
    const type = (this.resourceType || '').toLowerCase();
    if (type === 'issue' || type === 'changeset') { return true; }
    if (type === 'comparison') {
      const comparisonTo = this.resolveComparisonToForDelete();
      return Number.isFinite(comparisonTo) && (comparisonTo as number) > 0;
    }
    return false;
  }

  get deleteModalTitle(): string {
    return 'Excluir review';
  }

  get deleteActionVerb(): string {
    return 'excluir';
  }

  get deleteActionLabel(): string {
    return 'Excluir';
  }

  get deleteActionLoadingLabel(): string {
    return 'Excluindo...';
  }

  get deleteDescription(): string {
    return this.isSharedReviewContext
      ? 'Este review pode ser compartilhado. Se houver outros usuários associados, ele será removido apenas da sua lista. Se você for o último usuário associado, o review será excluído definitivamente.'
      : 'Este review não é compartilhado. A exclusão é definitiva e não poderá ser desfeita.';
  }

  get deleteSuccessMessage(): string {
    return this.isSharedReviewContext ? 'Review removido da sua lista' : 'Review excluído com sucesso';
  }

  get deleteBlockedMessage(): string {
    return 'Review em processamento não pode ser excluído. Aguarde a conclusão.';
  }

  get deleteDisplayName(): string {
    // sourceDisplayName é sobrescrito com files[0].name para issue/changeset/standalone,
    // portanto esses tipos precisam de campo específico.
    // Para comparison, sourceDisplayName vem do estado de navegação e não é sobrescrito.
    if (this.isIssue) {
      return this.resourceId || this.reviewSourceName || '-';
    }
    if (this.isChangeset) {
      return this.reviewChangesetNumber || this.resourceId || '-';
    }
    if (this.isGitChange) {
      return this.gitChangeLabel || '-';
    }
    // Comparison e Standalone: igual ao grid (sourceDisplayName || resourceId)
    return this.sourceDisplayName || this.resourceId || '-';
  }

  private resolveComparisonToForDelete(): number | undefined {
    if (Number.isFinite(this.reviewTo)) { return this.reviewTo as number; }

    const parts = (this.resourceId || '').split('_');
    if (parts.length < 2) { return undefined; }
    const parsedTo = Number(parts[1]);
    return Number.isFinite(parsedTo) ? parsedTo : undefined;
  }

  private destroy$ = new Subject<void>();
  activeAnnotationLine: number | null = null;

  // Overview panel (substitui a view zone do Monaco — colapsável via Angular)
  overviewCollapsed = false;
  toggleOverview(): void { this.overviewCollapsed = !this.overviewCollapsed; }

  // Metadados do review — passados via router state.
  reviewRequestedBy = '';
  reviewRequestedAt = '';
  reviewLastActivityAt = '';
  reviewLastRetryBy = '';
  reviewLastRetryAt = '';
  sourceDisplayName = '';
  /**
   * Origem do código revisado, como o usuário a vê: TFVC, Upload ou o provedor git.
   *
   * Vazio só quando o repositório de um review git foi descadastrado depois — aí o rótulo
   * não aparece, em vez de mostrar um campo em branco.
   */
  reviewProviderLabel = '';
  reviewSourceName = '';
  reviewSourcePath = '';
  reviewChangesetNumber = '';
  reviewStatusDerived = '';
  reviewCurrentStep = '';
  reviewJobStatus = '';
  reviewFrom: number | null = null;
  reviewTo: number | null = null;
  diffFromChangeset: number | null = null;
  diffToChangeset: number | null = null;
  /**
   * Se a versão revisada ainda é a vigente na origem (TFVC ou git). CHECKING_FRESHNESS
   * enquanto o histórico carrega; null quando não se aplica ou não dá para afirmar.
   */
  changesetFreshness: ChangesetFreshness | null = null;
  private changesetFreshnessSub?: Subscription;
  /** Uma página basta: o selo diz "N+ depois" quando a versão revisada fica além dela. */
  private readonly changesetFreshnessPageSize = 20;
  /**
   * Onde conferir, no git, se o arquivo mudou depois do review: o repositório, a revisão
   * revisada e a branch de hoje (vazia = branch padrão, que é a de PR fechado ou mergeado).
   * Vem da lista de arquivos; null quando falta dado para conferir. No PR a revisão fica
   * vazia: ela é por arquivo (`ReviewFile.revision`), a do diff que a tela mostra.
   */
  private gitFreshnessSource: { slug: string; revision: string; branch: string } | null = null;
  annotationsPanelCollapsed = false;
  private readonly annotationsPanelPrefKey = 'smartReview.diff.annotationsPanelCollapsed';
  private pendingInitialScrollToTop = false;
  private pendingFindingId = '';
  private pendingFindingLine: number | null = null;
  private scrollToTopTimers: Array<ReturnType<typeof setTimeout>> = [];

  get isTechnicalDocProcessing(): boolean {
    if (this.technicalDocActionLoading || !!this.technicalDocPollingJobId) {
      return true;
    }
    const status = this.normalizeStatus(this.technicalDocJob?.status);
    return status === 'processing' || status === 'running' || status === 'queued';
  }

  get reviewChangesetLabel(): string {
    const typeL = (this.resourceType || '').toLowerCase();
    if (typeL === 'standalone') { return 'n/d'; }
    if (typeL === 'issue') { return 'Issue'; }
    if (typeL === 'changeset') { return this.reviewChangesetNumber || this.resourceId || 'n/d'; }
    if (this.isGitChange) { return this.gitChangeLabel || 'n/d'; }
    // Para comparison: extrai apenas o trecho de changesets (sem prefixo "cs").
    const parsed = this.parseComparisonDisplayName();
    return parsed.changesetLabel || 'n/d';
  }

  get reviewSourceLabel(): string {
    if (this.isIssue) {
      return this.resourceId || '-';
    }
    if (this.isGitChange) {
      return this.gitChangeLabel || '-';
    }
    const selectedName = this.selectedFile?.name || this.selectedFile?.path?.split('/').pop();
    return selectedName || this.reviewSourceName || this.resourceId || '-';
  }

  get showRetryActor(): boolean {
    const retryBy = (this.reviewLastRetryBy || '').trim();
    const requestedBy = (this.reviewRequestedBy || '').trim();
    return !!retryBy && retryBy !== requestedBy;
  }

  get retryInfoLabel(): string {
    return this.showRetryActor ? 'Reprocessado por' : 'Reprocessado em';
  }

  get currentSourcePath(): string {
    return this.selectedFile?.path || this.reviewSourcePath || '';
  }

  get diffChangesetContextLabel(): string {
    if (this.isStandalone) { return ''; }

    const from = this.diffFromChangeset;
    const to = this.diffToChangeset;
    const hasFrom = Number.isFinite(from);
    const hasTo = Number.isFinite(to);

    if (hasFrom && hasTo && (from as number) > 0 && (to as number) === 0) {
      return `Changeset: ${from} -> local`;
    }

    if (hasFrom && hasTo && (from as number) > 0 && (to as number) > 0) {
      return `Changeset: ${from} -> ${to}`;
    }

    if ((!hasFrom || (from as number) === 0) && hasTo && (to as number) > 0) {
      return `Changeset: ${to}`;
    }

    return '';
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location,
    private smartReviewService: ReviewService,
    private jobsService: JobsService,
    private mainService: MainService,
    private permissions: SmartReviewPermissionsService,
    public notificationServiceHM: NotificationServiceHM,
    private cdr: ChangeDetectorRef,
    private tfvcService: TfvcService,
    private gitService: GitService
  ) {}

  /** Gerar/regerar o entendimento técnico exige `smartreview.run`. */
  get canRun(): boolean {
    return this.permissions.hasRun();
  }

  goBack(): void {
    this.location.back();
  }

  openDeleteReviewModal(): void {
    if (!this.canDeleteReview) {
      this.notificationServiceHM.showToast(
        this.deleteBlockedMessage,
        'warning'
      );
      return;
    }
    if (!this.deleteReviewModal) {
      this.notificationServiceHM.showToast(
        'Não foi possível abrir a confirmação de exclusão. Atualize a tela e tente novamente.',
        'warning'
      );
      return;
    }
    this.updateDeleteReviewModalActions();
    this.deleteReviewModal.open();
  }

  closeDeleteReviewModal(): void {
    if (this.deleteReviewLoading) { return; }
    this.deleteReviewModal?.close();
  }

  openOverallFeedbackModal(): void {
    if (!this.canShowOverallFeedbackQuickAction) { return; }
    this.overallFeedbackModal?.open();
    // O PO foca o primeiro botão do conteúdo, e as opções de veredito são po-tag: o foco caía
    // na lixeira de "Outras avaliações" (com tooltip aberto, e Enter pedindo remoção). Vai
    // para o fechar, depois do foco do próprio PO.
    setTimeout(() => {
      this.overallFeedbackModal?.modalContent?.nativeElement.querySelector('button')?.focus();
    });
  }

  closeOverallFeedbackModal(): void {
    this.overallFeedbackModal?.close();
  }

  confirmDeleteReview(): void {
    if (!this.resourceType || !this.resourceId || this.deleteReviewLoading) { return; }
    if (!this.isReviewTerminalForDelete()) {
      this.notificationServiceHM.showToast(
        this.deleteBlockedMessage,
        'warning'
      );
      this.closeDeleteReviewModal();
      return;
    }

    const deleteKey = buildReviewDeleteKey(this.resourceType, this.resourceId);
    if (this.deleteInFlightKey === deleteKey) { return; }

    this.deleteInFlightKey = deleteKey;
    this.deleteReviewLoading = true;
    this.updateDeleteReviewModalActions();
    this.cdr.markForCheck();

    this.smartReviewService
      // `?requestedBy=` (remover review de outro usuário) só é aceito com `smartreview.admin`.
      .deleteReview(
        this.resourceType as Review['resourceType'],
        this.resourceId,
        this.permissions.hasAdmin() ? this.reviewRequestedBy : undefined
      )
      .pipe(
        finalize(() => {
          this.deleteReviewLoading = false;
          this.deleteInFlightKey = null;
          this.updateDeleteReviewModalActions();
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.deleteReviewModal?.close();
          this.notificationServiceHM.showToast(this.deleteSuccessMessage, 'success');
          if (!this.modalMode) {
            this.router.navigate(['/menu/smart-review/reviews']);
          }
        },
        error: (error: unknown) => {
          this.notificationServiceHM.showToast(getDeleteReviewErrorMessage(error), 'error');
        }
      });
  }

  private updateBreadcrumb(): void {
    this.breadcrumb = {
      items: [
        { label: 'Reviews', link: '/menu/smart-review/reviews' },
        { label: this.breadcrumbLabel }
      ]
    };
  }

  private loadAnnotationsPanelPreference(): void {
    const stored = localStorage.getItem(this.annotationsPanelPrefKey);
    this.annotationsPanelCollapsed = stored === 'true';
  }

  private persistAnnotationsPanelPreference(): void {
    localStorage.setItem(this.annotationsPanelPrefKey, String(this.annotationsPanelCollapsed));
  }

  setAnnotationsPanelCollapsed(collapsed: boolean): void {
    if (this.annotationsPanelCollapsed === collapsed) { return; }
    this.annotationsPanelCollapsed = collapsed;
    this.persistAnnotationsPanelPreference();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.clearScrollToTopTimers();
    this.changesetFreshnessSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.loadAnnotationsPanelPreference();
    this.mainService.validSession();
    // Lê metadados passados pelo router state.
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state ?? (typeof window !== 'undefined' ? (window.history?.state ?? {}) : {});
    this.reviewRequestedBy = state['requestedBy'] ?? '';
    this.reviewRequestedAt = state['requestedAt'] ?? '';
    this.reviewLastActivityAt = state['lastActivityAt'] ?? state['updatedAt'] ?? '';
    this.reviewLastRetryBy = state['lastRetryBy'] ?? '';
    this.reviewLastRetryAt = state['lastRetryAt'] ?? '';
    this.sourceDisplayName = state['sourceDisplayName'] ?? '';
    this.reviewProviderLabel = sourceProviderLabel(state['sourceProvider']);
    this.reviewSourceName  = state['sourceName'] ?? '';
    this.reviewSourcePath  = '';
    this.reviewChangesetNumber = String(state['resourceId'] ?? '');
    this.reviewStatusDerived = state['statusDerived'] ?? '';
    this.reviewCurrentStep = state['currentStep'] ?? '';
    this.reviewJobStatus = state['reviewJobStatus'] ?? '';
    this.reviewFrom = state['from'] != null ? Number(state['from']) : null;
    this.reviewTo   = state['to'] != null ? Number(state['to']) : null;
    // No modo modal o detalhe é dirigido por loadForModal() (sem rota).
    if (this.modalMode) {
      return;
    }

    (this.activatedRoute.fragment ?? of(null)).pipe(takeUntil(this.destroy$)).subscribe(fragment => {
      this.pendingFindingId = this.extractFindingIdFromFragment(fragment);
      this.pendingFindingLine = null;
      if (this.pendingFindingId && this.answerItems.length) {
        this.focusFindingFromHash(true);
        this.cdr.markForCheck();
      }
    });
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.resourceType = params['type'];
      this.resourceId = params['id'];
      if (!this.reviewChangesetNumber && (this.resourceType || '').toLowerCase() === 'changeset') {
        this.reviewChangesetNumber = this.resourceId;
      }
      this.updateBreadcrumb();
      this.cdr.markForCheck();
      this.loadData();
    });
  }

  /**
   * Carrega o review no modo modal (sem rota) — usado por telas que embutem o
   * detalhe, como a curadoria ao abrir o "review original".
   */
  loadForModal(resourceType: string, resourceId: string, findingId = ''): void {
    this.resourceType = resourceType;
    this.resourceId = resourceId;
    this.pendingFindingId = findingId;
    this.pendingFindingLine = null;
    this.cdr.markForCheck();
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.pendingInitialScrollToTop = true;
    this.scrollPageToTop();
    this.importedMode = false;
    this.technicalDocAvailable = false;
    this.technicalDocActionLoading = false;
    this.technicalDocAnswerItems = [];
    this.technicalDocAnswer = null;
    this.technicalDocJob = null;
    this.technicalDocStatusMessage = '';
    this.technicalDocPollingJobId = null;
    this.technicalDocChecked = false;
    this.diffFromChangeset = null;
    this.diffToChangeset = null;
    this.resetChangesetFreshness();
    // Quem escreve `pullState` é o callback do getFiles, e ele nem é chamado quando a
    // busca de arquivos é curto-circuitada. Sem zerar aqui, sair de um PR mergeado para
    // outro review na MESMA instância do componente — troca de parâmetro de rota — levava
    // `isPullClosed` do anterior, e a publicação sumia sem motivo.
    this.pullState = '';
    this.gitFreshnessSource = null;
    this.reviewAccessDenied = false;
    this.reviewAccessDeniedMessage = '';
    this.reviewAccessDeniedCode = '';
    this.answerItems = [];
    this.answer = null;
    this.answerItemByFile.clear();
    this.answersLoading = true;
    this.answersLoadError = false;
    this.onlyFilesWithFindings = false;
    this.filesWithFindingsCount = 0;
    const seq = ++this.loadSeq;

    // Arquivos, status e feedback são leves; as respostas trazem a análise de TODOS os
    // arquivos e passam de 1 MB num PR grande. Por isso a tela não espera por elas: combo
    // e diff aparecem com os arquivos, e a análise entra quando chega.
    let structureReady = false;
    let answersResponse: ReviewAnswersResponse<ReviewAnswer> | null | undefined;
    const applyAnswersWhenReady = () => {
      if (seq !== this.loadSeq || !structureReady || answersResponse === undefined) { return; }
      this.applyAnswers(answersResponse?.items ?? []);
    };

    forkJoin({
      files: this.smartReviewService
        .getFiles(
          this.resourceType,
          this.resourceId,
          this.reviewStatusDerived,
          this.reviewCurrentStep,
          this.reviewJobStatus,
          meta => {
            this.pullState = meta.state;
            this.gitFreshnessSource = this.resolveGitFreshnessSource(meta);
          }
        )
        .pipe(catchError(error => {
          this.markReviewAccessDenied(error);
          return of([]);
        })),
      reviewStatus: this.smartReviewService.getReviewStatus(this.resourceId, this.resourceType)
        .pipe(catchError(() => of(null))),
      feedback: this.smartReviewService.getReviewFeedback(this.resourceType, this.resourceId)
        .pipe(catchError(() => of({ total: 0, items: [] })))
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ files, reviewStatus, feedback }) => {
        if (seq !== this.loadSeq) { return; }
        if (reviewStatus) {
          // O provedor vem da resposta, e não só do router state: abrir o detalhe por URL
          // (F5, link compartilhado) ou dentro do modal da curadoria não passa state
          // nenhum, e o rótulo sumia justamente nos dois caminhos que não vêm da lista.
          // Pelo mesmo motivo quem pediu e quando: sem isto apareciam como "n/d".
          this.reviewProviderLabel = sourceProviderLabel(reviewStatus.sourceProvider) || this.reviewProviderLabel;
          this.reviewStatusDerived = reviewStatus.statusDerived ?? this.reviewStatusDerived;
          this.reviewCurrentStep = reviewStatus.currentStep ?? this.reviewCurrentStep;
          this.reviewJobStatus = reviewStatus.jobs?.review?.status ?? this.reviewJobStatus;
          this.reviewRequestedBy = reviewStatus.requestedBy || this.reviewRequestedBy;
          this.reviewRequestedAt = reviewStatus.requestedAt || this.reviewRequestedAt;
          this.reviewLastActivityAt = reviewStatus.lastActivityAt
            || reviewStatus.jobs?.review?.updatedAt
            || this.reviewLastActivityAt;
          this.reviewLastRetryBy = reviewStatus.jobs?.review?.lastRetryBy || this.reviewLastRetryBy;
          this.reviewLastRetryAt = reviewStatus.jobs?.review?.lastRetryAt || this.reviewLastRetryAt;
          this.markFailedReviewClosed();
        }
        if (this.reviewAccessDenied) {
          this.applyReviewAccessDenied();
          return;
        }
        this.applyFeedback(feedback.items);
        this.files = files;
        if (!this.sourceDisplayName && files.length > 0) {
          this.sourceDisplayName = files[0].name || files[0].path || this.resourceId;
          this.updateBreadcrumb();
        }
        if (!this.reviewSourcePath && files.length > 0) {
          this.reviewSourcePath = files[0].path || files[0].name || '';
        }
        this.selectedFile = null;
        this.selectedFileValue = '';
        this.rebuildFileOptions();

        // Com âncora de achado no hash, o arquivo certo só se sabe pelas respostas; sem
        // arquivos (standalone, comparison), o que carregar também depende delas.
        if (files.length > 0 && !this.pendingFindingId) {
          this.switchFile(files[0], true);
        }
        structureReady = true;
        this.updateDeleteReviewModalActions();
        this.cdr.markForCheck();
        applyAnswersWhenReady();
      },
      error: () => {
        if (seq !== this.loadSeq) { return; }
        this.notificationServiceHM.showToast('Erro ao carregar detalhes do review', 'error');
        this.loading = false;
        this.answersLoading = false;
        this.cdr.markForCheck();
        this.consumeInitialScrollToTop();
      }
    });

    this.smartReviewService.getAnswers<ReviewAnswer>(this.resourceType, this.resourceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          answersResponse = response ?? null;
          applyAnswersWhenReady();
        },
        error: error => {
          if (seq !== this.loadSeq) { return; }
          if (this.markReviewAccessDenied(error)) {
            if (structureReady) { this.applyReviewAccessDenied(); }
            return;
          }
          this.notificationServiceHM.showToast('Erro ao carregar a análise do review', 'error');
          this.answersLoadError = true;
          answersResponse = null;
          applyAnswersWhenReady();
        }
      });
  }

  private applyReviewAccessDenied(): void {
    this.files = [];
    this.fileOptions = [];
    this.visibleFiles = [];
    this.answerItems = [];
    this.answer = null;
    this.answersLoading = false;
    this.loading = false;
    this.updateDeleteReviewModalActions();
    this.cdr.markForCheck();
    this.consumeInitialScrollToTop();
  }

  /** Aplica as respostas que chegaram depois dos arquivos. */
  private applyAnswers(items: ReviewAnswerItem[]): void {
    this.answerItems = items;
    this.answersLoading = false;
    this.indexAnswers();
    this.filesWithFindingsCount = this.files.filter(file => this.fileHasFindings(file)).length;

    if (this.focusFindingFromHash(true)) {
      // A navegação por hash seleciona arquivo/answer e dispara o carregamento necessário.
    } else if (this.selectedFile) {
      this.selectAnswerForFile(this.selectedFile);
    } else if (this.files.length > 0) {
      this.switchFile(this.files[0], true);
    } else {
      this.selectedAnswerItem = this.answerItems[0] ?? null;
      this.answer = this.selectedAnswerItem?.answer ?? null;
      this.overviewCollapsed = !this.hasFindings(this.answer);
      if (this.isStandalone) {
        this.loadSource('');
      } else if (this.isFailedReview) {
        // Review com erro sem arquivo nenhum: não há diff a mostrar. Pedir o diff sem
        // `path` só devolvia erro num recurso de vários arquivos (issue, PR, changeset).
        this.loading = false;
      } else {
        this.loadDiff();
      }
    }
    this.updateDeleteReviewModalActions();
    this.cdr.markForCheck();
  }

  /** Review que terminou com erro — status geral ou da etapa de review. */
  get isFailedReview(): boolean {
    return FAILED_REVIEW_STATUSES.includes((this.reviewStatusDerived || '').toLowerCase().trim())
      || FAILED_REVIEW_STATUSES.includes((this.reviewJobStatus || '').toLowerCase().trim());
  }

  /** Aviso do review com erro que abriu: o que está na tela pode não ser o resultado atual. */
  get failedReviewNotice(): { title: string; text: string } {
    const derived = (this.reviewStatusDerived || '').toLowerCase().trim();
    const status = FAILED_REVIEW_STATUSES.includes(derived) ? derived : (this.reviewJobStatus || '').toLowerCase().trim();
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
    return {
      title: label ? `Último processamento com erro (${label})` : 'Último processamento com erro',
      text: 'O conteúdo abaixo é o que ficou gravado e pode ser de uma execução anterior. ' +
        'Reprocesse para atualizar.'
    };
  }

  /** Nome do repositório para o aviso de acesso negado; vazio quando não dá para ler. */
  get deniedRepositoryLabel(): string {
    const id = this.resourceId ?? '';
    return parsePullResourceId(id)?.slug ?? parseCommitResourceId(id)?.slug ?? '';
  }

  /**
   * O aviso de acesso negado. O título sai do código que a API mandou; o texto é a
   * mensagem da própria API — a mesma que a extensão mostra —, para o motivo e o que fazer
   * terem uma redação só, mantida no backend.
   */
  get reviewAccessNotice(): { title: string; text: string } {
    const repository = this.deniedRepositoryLabel;
    const text = this.reviewAccessDeniedMessage || 'O conteúdo deste review não está disponível para o seu usuário.';
    switch (this.reviewAccessDeniedCode) {
      case 'repository_access_denied':
        return { title: repository ? `Sem acesso ao repositório ${repository}` : 'Sem acesso ao repositório deste review', text };
      case 'tfvc_access_denied':
        return { title: 'Sem acesso ao TFVC do Protheus', text };
      case 'review_not_visible':
        return { title: 'Review de outra pessoa', text };
      case REVIEW_FAILED_CODE:
        return { title: 'Review com erro', text };
    }
    return { title: repository ? `Sem acesso ao repositório ${repository}` : 'Sem acesso a este review', text };
  }

  /**
   * Review com erro fica fechado para quem não tem visão global, venha de onde vier —
   * lista, card do dashboard ou link colado. É a mesma regra do menu da lista
   * (`reviewOpenBlock`); sem ela aqui, o dashboard abria o review que a lista fechava.
   * Usa o caminho do acesso negado: esconde o conteúdo e explica o motivo. Só o erro
   * fecha aqui — em andamento a tela segue como era, acompanhando o processamento.
   */
  private markFailedReviewClosed(): void {
    if (reviewOpenBlock(this.reviewStatusDerived, this.reviewCurrentStep, this.permissions.hasGlobalView()) !== 'failed') {
      return;
    }
    this.reviewAccessDenied = true;
    this.reviewAccessDeniedCode = REVIEW_FAILED_CODE;
    this.reviewAccessDeniedMessage =
      'Este review terminou com erro e fica disponível quando for reprocessado.';
  }

  /**
   * Marca o acesso negado quando a resposta é 403. Devolve se marcou, para o chamador
   * decidir entre seguir e propagar o erro.
   */
  private markReviewAccessDenied(error: unknown): boolean {
    if (getHttpStatus(error) !== 403) {
      return false;
    }
    this.reviewAccessDenied = true;
    // O texto vem da API: o provedor negar pede acesso lá; review de colega pede o mesmo
    // review; falta de TFVC é outra coisa. A tela não tem como distinguir sozinha.
    this.reviewAccessDeniedMessage = getApiErrorMessage(error, '');
    this.reviewAccessDeniedCode = getBackendErrorCode(error);
    return true;
  }

  setAnalysisMode(mode: 'code_review' | 'technical_doc'): void {
    if (this.analysisMode === mode) { return; }
    this.analysisMode = mode;
    this.scrollPageToTop();
    if (mode === 'technical_doc' && !this.technicalDocChecked) {
      this.technicalDocChecked = true;
      this.loadTechnicalDocAvailability(false);
    }
  }

  onTechnicalDocTabClick(): void {
    this.setAnalysisMode('technical_doc');
  }

  requestTechnicalDocGeneration(): void {
    if (this.isTechnicalDocProcessing) { return; }
    this.notificationServiceHM.showToast('Iniciando geração do entendimento técnico...', 'success');
    this.generateTechnicalDocFromCodeReview();
  }


  private loadTechnicalDocAvailability(openTechnicalTabWhenAvailable = false): void {
    if (!this.resourceType || !this.resourceId) { return; }

    this.jobsService.getJobs({
      promptType: 'technical_doc',
      resourceId: this.resourceId,
      jobType: 'assistant',
      limit: 50,
      offset: 0
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: jobsResponse => {
          const latest = this.pickLatestJob(jobsResponse?.items ?? []);
          this.technicalDocJob = latest;

          if (!latest) {
            this.technicalDocAvailable = false;
            this.technicalDocAnswer = null;
            this.technicalDocAnswerItems = [];
            this.technicalDocStatusMessage = 'Entendimento técnico ainda não foi gerado para este review.';
            this.cdr.markForCheck();
            return;
          }

          const status = this.normalizeStatus(latest.status);
          if (status !== 'done') {
            this.technicalDocAvailable = false;
            this.technicalDocAnswer = null;
            this.technicalDocAnswerItems = [];
            if (status === 'processing' || status === 'running' || status === 'queued') {
              this.technicalDocStatusMessage = 'Entendimento técnico em processamento...';
            } else if (status === 'failed' || status === 'dead') {
              this.technicalDocStatusMessage = 'Entendimento técnico não foi gerado para este review.';
            } else {
              this.technicalDocStatusMessage = 'Entendimento técnico não foi gerado para este review.';
            }
            if ((status === 'processing' || status === 'running' || status === 'queued') && latest.jobId) {
              this.startTechnicalDocPolling(latest.jobId, false);
            }
            this.cdr.markForCheck();
            return;
          }

          this.loadTechnicalDoc();
          if (openTechnicalTabWhenAvailable) {
            this.analysisMode = 'technical_doc';
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.technicalDocAvailable = false;
          this.technicalDocAnswer = null;
          this.technicalDocAnswerItems = [];
          this.technicalDocStatusMessage = 'Entendimento técnico não foi gerado para este review.';
          this.cdr.markForCheck();
        }
      });
  }

  loadTechnicalDoc(): void {
    if (!this.resourceType || !this.resourceId) { return; }
    this.technicalDocLoading = true;
    this.smartReviewService.getTechnicalDocAnswers(this.resourceType, this.resourceId)
      .pipe(takeUntil(this.destroy$), finalize(() => { this.technicalDocLoading = false; this.cdr.markForCheck(); }))
      .subscribe({
        next: response => {
          this.technicalDocAnswerItems = response?.items ?? [];
          this.technicalDocAnswer = this.extractTechnicalDocAnswer(this.technicalDocAnswerItems);
          this.technicalDocAvailable = !!this.technicalDocAnswer;

          if (this.technicalDocAnswer) {
            this.technicalDocStatusMessage = '';
            this.technicalDocPollingJobId = null;
            this.loadTechnicalDocJobStatus();
            this.cdr.markForCheck();
            return;
          }

          this.technicalDocAvailable = false;
          this.loadTechnicalDocJobStatus();
          this.cdr.markForCheck();
        },
        error: () => {
          this.technicalDocAvailable = false;
          this.technicalDocStatusMessage = 'Entendimento técnico não foi gerado para este review.';
          this.cdr.markForCheck();
        }
      });
  }

  refreshTechnicalDoc(): void {
    this.technicalDocChecked = true;
    if (this.technicalDocAvailable) {
      this.loadTechnicalDoc();
      return;
    }
    this.loadTechnicalDocAvailability(false);
  }

  generateTechnicalDocFromCodeReview(): void {
    if (!this.resourceType || !this.resourceId || this.technicalDocActionLoading) { return; }

    const request: PromptGenerationRequest = {
      inline: { baseRadius: 0, maxLines: 0 },
      promptType: 'technical_doc'
    };

    this.technicalDocActionLoading = true;

    this.smartReviewService.generatePrompt(this.resourceType, this.resourceId, request).pipe(
      concatMap(() => this.smartReviewService.runAssistant(this.resourceType, this.resourceId, 'technical_doc', true)),
      takeUntil(this.destroy$),
      finalize(() => { this.technicalDocActionLoading = false; this.cdr.markForCheck(); })
    ).subscribe({
      next: assistantJob => {
        this.technicalDocJob = this.mapAssistantJob(assistantJob);
        const jobId = assistantJob?.jobId;
        const status = this.normalizeStatus(assistantJob?.status);

        if (status === 'done') {
          this.notificationServiceHM.showToast('Entendimento técnico concluído', 'success');
          this.loadTechnicalDocAvailability(true);
          return;
        }

        if (!jobId) {
          this.notificationServiceHM.showToast('Processamento técnico iniciado', 'success');
          this.cdr.markForCheck();
          return;
        }

        this.notificationServiceHM.showToast('Entendimento técnico em processamento...', 'success');
        this.startTechnicalDocPolling(jobId, true, true);
        this.cdr.markForCheck();
      },
      error: error => {
        this.notificationServiceHM.showToast(getApiErrorMessage(error, 'Erro ao gerar entendimento técnico'), 'error');
        this.cdr.markForCheck();
      }
    });
  }

  retryTechnicalDoc(): void {
    const jobId = this.technicalDocJob?.jobId;
    if (!jobId || this.technicalDocActionLoading) {
      this.notificationServiceHM.showToast('Job técnico não encontrado para reprocessar', 'warning');
      return;
    }

    this.technicalDocActionLoading = true;

    this.jobsService.retryJob(jobId).pipe(
      takeUntil(this.destroy$),
      finalize(() => { this.technicalDocActionLoading = false; this.cdr.markForCheck(); })
    ).subscribe({
      next: result => {
        const retriedJobId = result?.jobId ?? result?.id ?? jobId;
        this.notificationServiceHM.showToast('Reprocessamento técnico iniciado...', 'success');
        this.startTechnicalDocPolling(retriedJobId, true, true);
        this.cdr.markForCheck();
      },
      error: error => {
        if (isServiceUnavailable(error)) {
          showServiceUnavailableToast(this.notificationServiceHM, error);
        } else {
          this.notificationServiceHM.showToast(getApiErrorMessage(error, 'Erro ao reprocessar entendimento técnico'), 'error');
        }
        this.cdr.markForCheck();
      }
    });
  }

  getTechnicalDocStatusType(status?: string): string {
    const normalized = this.normalizeStatus(status);
    if (normalized === 'done') { return 'success'; }
    if (normalized === 'failed' || normalized === 'dead') { return 'danger'; }
    if (normalized === 'processing' || normalized === 'running') { return 'info'; }
    if (normalized === 'queued') { return 'warning'; }
    return 'warning';
  }

  isHttpLink(url?: string): boolean {
    return /^https?:\/\//i.test(url ?? '');
  }

  private loadTechnicalDocJobStatus(): void {
    if (!this.resourceId) { return; }

    this.jobsService.getJobs({
      promptType: 'technical_doc',
      resourceId: this.resourceId,
      jobType: 'assistant',
      limit: 50,
      offset: 0
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        const latest = this.pickLatestJob(response?.items ?? []);
        this.technicalDocJob = latest;

        if (!latest) {
          this.technicalDocStatusMessage = this.technicalDocAvailable
            ? ''
            : 'Entendimento técnico ainda não disponível. Aguardando processamento pelo backend.';
          this.cdr.markForCheck();
          return;
        }

        const status = this.normalizeStatus(latest.status);
        if (status === 'done') {
          this.technicalDocStatusMessage = this.technicalDocAvailable
            ? ''
            : 'Job concluído. Atualize para carregar a resposta.';
          this.cdr.markForCheck();
          return;
        }
        if (status === 'processing' || status === 'running' || status === 'queued') {
          this.technicalDocStatusMessage = 'Entendimento técnico em processamento...';
          if (latest.jobId) {
            this.startTechnicalDocPolling(latest.jobId, false, false);
          }
          this.cdr.markForCheck();
          return;
        }
        if (status === 'failed' || status === 'dead') {
          this.technicalDocStatusMessage = `Falha no processamento técnico${latest.error ? ': ' + latest.error : ''}.`;
          this.cdr.markForCheck();
          return;
        }

        this.technicalDocStatusMessage = `Status do processamento técnico: ${latest.status || 'n/d'}.`;
        this.cdr.markForCheck();
      },
      error: () => {
        this.technicalDocStatusMessage = 'Não foi possível consultar o status do processamento técnico.';
        this.cdr.markForCheck();
      }
    });
  }

  private startTechnicalDocPolling(
    jobId: string,
    openTechnicalTabWhenDone = false,
    notifyOnDone = false
  ): void {
    if (!jobId || this.technicalDocPollingJobId === jobId) { return; }
    this.technicalDocPollingJobId = jobId;

    let attempts = 0;
    let lastJob: SmartReviewJob | null = null;
    this.jobsService.getJobStatus(jobId).pipe(
      expand(job => {
        attempts++;
        if (this.isTerminalJobStatus(job.status) || attempts >= MAX_TECH_DOC_POLL_ATTEMPTS) {
          return EMPTY;
        }
        return timer(this.getTechnicalDocPollIntervalMs(attempts)).pipe(
          concatMap(() => this.jobsService.getJobStatus(jobId))
        );
      }),
      takeWhile(job => !this.isTerminalJobStatus(job.status), true),
      takeUntil(this.destroy$),
      catchError(() => of(null))
    ).subscribe({
      next: job => {
        if (!job) { return; }
        lastJob = job;
        this.technicalDocJob = job;

        const status = this.normalizeStatus(job.status);
        if (status === 'done') {
          this.technicalDocPollingJobId = null;
          this.technicalDocStatusMessage = 'Entendimento técnico concluído.';
          if (notifyOnDone) {
            this.notificationServiceHM.showToast('Entendimento técnico concluído', 'success');
          }
          this.loadTechnicalDocAvailability(openTechnicalTabWhenDone);
          this.cdr.markForCheck();
          return;
        }

        if (FAILED_JOB_STATUSES.includes(status)) {
          this.technicalDocPollingJobId = null;
          this.technicalDocStatusMessage = `Falha no processamento técnico${job.error ? ': ' + job.error : ''}.`;
          this.notificationServiceHM.showToast('Falha no processamento do entendimento técnico', 'error');
        }
        this.cdr.markForCheck();
      },
      complete: () => {
        const finalStatus = this.normalizeStatus(lastJob?.status);
        if (finalStatus && !this.isTerminalJobStatus(finalStatus)) {
          // Polling esgotou sem o job terminar
          this.technicalDocPollingJobId = null;
          this.technicalDocStatusMessage = 'O processamento está demorando mais que o esperado. Use "Atualizar" para verificar o status.';
          this.notificationServiceHM.showToast('Entendimento técnico ainda em processamento. Atualize manualmente.', 'warning');
          this.cdr.markForCheck();
        }
      }
    });
  }

  private getTechnicalDocPollIntervalMs(attempts: number): number {
    if (attempts <= 3)  { return 20000; } // primeiras tentativas: 20s (IA demora pelo menos isso)
    return 30000;                         // fase principal: 30s
  }

  private extractTechnicalDocAnswer(items: ReviewAnswerItem<TechnicalDocAnswer>[]): TechnicalDocAnswer | null {
    const firstWithAnswer = items.find(item => item?.answer != null);
    return this.normalizeTechnicalDocAnswer(firstWithAnswer?.answer);
  }

  private normalizeTechnicalDocAnswer(raw: unknown): TechnicalDocAnswer | null {
    if (!raw) { return null; }
    if (typeof raw === 'object') { return raw as TechnicalDocAnswer; }
    if (typeof raw !== 'string') { return null; }

    const parsed = this.tryParseJson(raw);
    if (!parsed) { return null; }
    if (typeof parsed === 'string') {
      const nested = this.tryParseJson(parsed);
      return (nested && typeof nested === 'object') ? (nested as TechnicalDocAnswer) : null;
    }
    return (typeof parsed === 'object') ? (parsed as TechnicalDocAnswer) : null;
  }

  private tryParseJson(value: string): unknown | null {
    const trimmed = (value ?? '').replace(/^\uFEFF/, '').trim();
    if (!trimmed) { return null; }
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  private pickLatestJob(items: SmartReviewJob[]): SmartReviewJob | null {
    if (!Array.isArray(items) || !items.length) { return null; }
    const sorted = [...items].sort((a, b) => {
      const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bTime - aTime;
    });
    return sorted[0] ?? null;
  }

  private mapAssistantJob(job: SmartReviewAssistantJobResponse): SmartReviewJob {
    return {
      jobId: job.jobId,
      status: job.status,
      resourceId: job.resourceId,
      resourceType: job.resourceType,
      promptType: job.promptType,
      jobType: job.jobType,
      action: job.action,
      requestedBy: job.requestedBy,
      areaId: job.areaId,
      createdAt: job.createdAt,
      statusUrl: job.statusUrl
    };
  }

  private normalizeStatus(status?: string): string {
    return (status || '').toLowerCase().trim();
  }

  private isTerminalJobStatus(status?: string): boolean {
    return TERMINAL_JOB_STATUSES.includes(this.normalizeStatus(status));
  }

  selectAnswerForFile(file: ReviewFile): void {
    if (!this.answerItems.length) {
      this.selectedAnswerItem = null;
      this.answer = null;
      this.activeAnnotationLine = null;
      return;
    }

    // Cair na primeira resposta só faz sentido quando o review tem um arquivo só. Com
    // vários, arquivo sem resposta própria — o que o backend não envia ao modelo por não
    // ter linha alterada — exibia a análise de OUTRO arquivo sobre este diff.
    const match = this.resolveAnswerItemForFile(file);
    const singleFile = this.files.length <= 1;
    this.selectedAnswerItem = match || (singleFile ? this.answerItems[0] ?? null : null);
    this.answer = this.selectedAnswerItem?.answer ?? null;
    // Sem achados a Visão Geral só repete o "aprovado" dos chips e empurra o diff para
    // baixo da dobra. Recolhe; quem quiser ler o entendimento abre.
    this.overviewCollapsed = !this.hasFindings(this.answer);
    const targetFinding = this.findFindingInAnswer(this.answer, this.pendingFindingId);
    this.activeAnnotationLine = targetFinding?.line ?? this.getInitialAnnotationLine(this.answer);
    this.pendingFindingLine = this.pendingFindingId ? targetFinding?.line ?? null : this.pendingFindingLine;
  }

  private getInitialAnnotationLine(answer: ReviewAnswer | null): number | null {
    if (!answer) { return null; }

    const lines = [
      ...(answer.required_actions ?? []).map(action => action.line),
      ...(answer.suggestions ?? []).map(suggestion => suggestion.line)
    ].filter((line): line is number => typeof line === 'number' && Number.isFinite(line) && line > 0);

    if (!lines.length) { return null; }
    return Math.min(...lines);
  }

  private extractFindingIdFromFragment(fragment: string | null | undefined): string {
    const value = (fragment || '').trim();
    if (!value.startsWith('finding-')) { return ''; }
    try {
      return decodeURIComponent(value.slice('finding-'.length));
    } catch {
      return value.slice('finding-'.length);
    }
  }

  private focusFindingFromHash(forceReload = false): boolean {
    if (!this.pendingFindingId) { return false; }

    const target = this.findAnswerItemByFindingId(this.pendingFindingId);
    if (!target) {
      this.pendingFindingLine = null;
      return false;
    }

    this.annotationsPanelCollapsed = false;
    this.persistAnnotationsPanelPreference();
    this.selectedAnswerItem = target.item;
    this.answer = target.item.answer ?? null;
    this.activeAnnotationLine = target.line;
    this.pendingFindingLine = target.line;

    const file = this.resolveFileForAnswerItem(target.item);
    if (file) {
      this.switchFile(file, forceReload);
      return true;
    }

    this.loading = true;
    if (this.isStandalone) {
      this.loadSource(this.files[0]?.path ?? '');
    } else {
      this.loadDiff(this.files[0]?.path);
    }
    return true;
  }

  private findAnswerItemByFindingId(findingId: string): { item: ReviewAnswerItem; line: number } | null {
    if (!findingId) { return null; }

    for (const item of this.answerItems) {
      const match = this.findFindingInAnswer(item.answer ?? null, findingId);
      if (match) {
        return { item, line: match.line };
      }
    }
    return null;
  }

  private findFindingInAnswer(answer: ReviewAnswer | null, findingId: string): { line: number } | null {
    if (!answer || !findingId) { return null; }

    const requiredAction = (answer.required_actions ?? []).find(action => action.id === findingId);
    if (requiredAction?.line != null) {
      return { line: requiredAction.line };
    }

    const suggestion = (answer.suggestions ?? []).find(item => item.id === findingId);
    if (suggestion?.line != null) {
      return { line: suggestion.line };
    }

    return null;
  }

  private resolveFileForAnswerItem(answerItem: ReviewAnswerItem): ReviewFile | null {
    if (!this.files.length) { return null; }

    const scored = this.files
      .map((file, index) => ({ file, index, score: this.getAnswerItemMatchScore(answerItem, file) }))
      .filter(entry => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.index - b.index);

    if (scored.length) {
      return scored[0].file;
    }

    const answerIndex = this.answerItems.indexOf(answerItem);
    const hasPathMetadata = this.answerItems.some(item => !!(item.filePath || item.fileName));
    if (answerIndex >= 0 && answerIndex < this.files.length && (!hasPathMetadata || this.answerItems.length === this.files.length)) {
      return this.files[answerIndex];
    }

    return null;
  }

  private applyFeedback(items: ReviewFeedback[]): void {
    const map = new Map<string, ReviewFeedback>();
    const others = new Map<string, ReviewFeedback[]>();
    const othersOverall: ReviewFeedback[] = [];
    let overall: ReviewFeedback | null = null;

    for (const item of items ?? []) {
      // `editable` vem do servidor; ausente (backend antigo), a lista só trazia o próprio.
      const mine = item.editable !== false;
      if (item.findingKind === 'overall') {
        if (mine) { overall = item; } else { othersOverall.push(item); }
      } else if (item.findingId) {
        if (mine) {
          map.set(item.findingId, item);
        } else {
          others.set(item.findingId, [...(others.get(item.findingId) ?? []), item]);
        }
      }
    }

    this.feedbackByFindingId = map;
    this.overallFeedback = overall;
    this.otherFeedbackByFindingId = others;
    this.otherOverallFeedback = othersOverall;
  }

  /** Admin removeu a avaliação de outro usuário: some da lista, achado ou veredito. */
  onOtherFeedbackRemoved(removed: ReviewFeedback): void {
    if (removed.findingKind === 'overall') {
      this.otherOverallFeedback = this.otherOverallFeedback.filter(fb => fb.id !== removed.id);
    } else if (removed.findingId) {
      const others = new Map(this.otherFeedbackByFindingId);
      const remaining = (others.get(removed.findingId) ?? []).filter(fb => fb.id !== removed.id);
      if (remaining.length) { others.set(removed.findingId, remaining); } else { others.delete(removed.findingId); }
      this.otherFeedbackByFindingId = others;
    }
    this.cdr.markForCheck();
  }

  onFeedbackChanged(change: ReviewFeedbackChange): void {
    if (change.findingKind === 'overall') {
      this.overallFeedback = change.feedback;
    } else if (change.findingId) {
      const map = new Map(this.feedbackByFindingId);
      if (change.feedback) {
        map.set(change.findingId, change.feedback);
      } else {
        map.delete(change.findingId);
      }
      this.feedbackByFindingId = map;
    }
    this.cdr.markForCheck();
  }

  private normalizePath(value: string): string {
    return (value ?? '').replace(/\\/g, '/').toLowerCase().trim();
  }

  private isSameFile(a: ReviewFile | null | undefined, b: ReviewFile | null | undefined): boolean {
    if (!a || !b) { return false; }
    return this.normalizePath(a.path || a.name) === this.normalizePath(b.path || b.name);
  }

  private buildFileLabel(file: ReviewFile): string {
    const base = file.path || file.name || 'Arquivo';
    return base;
  }

  hasFindings(answer: ReviewAnswer | null | undefined): boolean {
    return !!(answer?.required_actions?.length || answer?.suggestions?.length);
  }

  /** Resolve a resposta de cada arquivo de uma vez: caminho exato por mapa, o resto por pontuação. */
  private indexAnswers(): void {
    this.answerItemByFile.clear();
    if (!this.answerItems.length) { return; }

    const candidates = this.answerItems.map(item => this.getAnswerItemCandidates(item));
    const byPath = new Map<string, ReviewAnswerItem>();
    candidates.forEach((c, index) => {
      for (const path of c.paths) {
        if (!byPath.has(path)) { byPath.set(path, this.answerItems[index]); }
      }
    });

    for (const file of this.files) {
      const exact = byPath.get(this.normalizePath(file.path || ''));
      this.answerItemByFile.set(file, exact ?? this.matchAnswerItemForFile(file, candidates));
    }
  }

  private fileHasFindings(file: ReviewFile): boolean {
    return this.hasFindings(this.answerItemByFile.get(file)?.answer);
  }

  /** Recria as opções do combo conforme o filtro. */
  private rebuildFileOptions(): void {
    const visible: ReviewFile[] = [];
    const options: { label: string; value: string }[] = [];
    this.fileByOptionValue.clear();
    this.files.forEach((file, index) => {
      if (this.onlyFilesWithFindings && !this.fileHasFindings(file)) { return; }
      const value = this.buildFileOptionValue(file, index);
      this.fileByOptionValue.set(value, file);
      visible.push(file);
      options.push({ label: this.buildFileLabel(file), value });
    });
    this.visibleFiles = visible;
    this.fileOptions = options;
    if (this.fileCombo) { this.fileCombo.removeInitialFilter = true; }
    if (this.selectedFile) {
      this.selectedFileValue = this.findOptionValueForFile(this.selectedFile);
    }
  }

  onOnlyFilesWithFindingsChange(enabled: boolean): void {
    if (this.onlyFilesWithFindings === enabled) { return; }
    this.onlyFilesWithFindings = enabled;
    this.rebuildFileOptions();
    // O arquivo aberto saiu da lista: abre o primeiro que ficou, em vez de deixar o combo vazio.
    if (enabled && this.selectedFile && !this.visibleFiles.includes(this.selectedFile) && this.visibleFiles.length) {
      this.switchFile(this.visibleFiles[0]);
    }
    this.cdr.markForCheck();
  }

  /**
   * O filtro só aparece quando muda a lista: com todos os arquivos com ações ou sugestões,
   * ou nenhum, ligar e desligar dá no mesmo. Ligado, fica visível para poder desligar.
   */
  get showFindingsFilter(): boolean {
    if (this.onlyFilesWithFindings) { return true; }
    return !this.answersLoading
      && this.filesWithFindingsCount > 0
      && this.filesWithFindingsCount < this.files.length;
  }

  /** "Todos (1.050)". */
  get allFilesFilterLabel(): string {
    return `Todos (${this.files.length.toLocaleString('pt-BR')})`;
  }

  /** "Com ações ou sugestões (636)". */
  get findingsFilterLabel(): string {
    return `Com ações ou sugestões (${this.filesWithFindingsCount.toLocaleString('pt-BR')})`;
  }

  /** "12 de 1.050" — onde o arquivo aberto está na lista que o combo mostra. */
  get filePositionLabel(): string {
    const total = this.visibleFiles.length;
    const index = this.selectedFile ? this.visibleFiles.indexOf(this.selectedFile) : -1;
    const totalLabel = total.toLocaleString('pt-BR');
    return index >= 0 ? `${(index + 1).toLocaleString('pt-BR')} de ${totalLabel}` : `${totalLabel} arquivos`;
  }

  private buildFileOptionValue(file: ReviewFile, index: number): string {
    const base = file.path || file.name || '';
    return `${index}:${base}`;
  }

  private resolveAnswerItemForFile(file: ReviewFile): ReviewAnswerItem | null {
    const cached = this.answerItemByFile.get(file);
    if (cached !== undefined) { return cached; }
    return this.matchAnswerItemForFile(file, this.answerItems.map(item => this.getAnswerItemCandidates(item)));
  }

  private matchAnswerItemForFile(
    file: ReviewFile,
    candidates: Array<{ paths: string[]; names: string[] }>
  ): ReviewAnswerItem | null {
    if (!this.answerItems.length) { return null; }

    const scored = this.answerItems
      .map((item, index) => ({ item, index, score: this.scoreAnswerCandidates(candidates[index], file) }))
      .filter(entry => entry.score > 0)
      .sort((a, b) => b.score - a.score || a.index - b.index);

    if (scored.length > 0) {
      return scored[0].item;
    }

    // Fallback para APIs antigas: quando não vier filePath/fileName, usa ordem do array.
    const fileIndex = this.files.findIndex(f => this.isSameFile(f, file));
    const hasPathMetadata = this.answerItems.some(item => !!(item.filePath || item.fileName));
    if (fileIndex >= 0 && fileIndex < this.answerItems.length && (!hasPathMetadata || this.answerItems.length === this.files.length)) {
      return this.answerItems[fileIndex];
    }

    return null;
  }

  private getAnswerItemMatchScore(item: ReviewAnswerItem, file: ReviewFile): number {
    return this.scoreAnswerCandidates(this.getAnswerItemCandidates(item), file);
  }

  private getAnswerItemCandidates(item: ReviewAnswerItem): { paths: string[]; names: string[] } {
    const paths = [
      item.filePath,
      item.path,
      item.sourcePath,
      item.file?.path,
      item.metadata?.filePath,
      item.metadata?.path
    ]
      .map((v: unknown) => this.normalizePath((v ?? '').toString()))
      .filter(Boolean);
    const names = [
      item.fileName,
      item.name,
      item.sourceName,
      item.file?.name,
      item.metadata?.fileName,
      item.metadata?.name
    ]
      .map((v: unknown) => this.normalizePath((v ?? '').toString()))
      .filter(Boolean);
    return { paths, names };
  }

  private scoreAnswerCandidates(candidates: { paths: string[]; names: string[] }, file: ReviewFile): number {
    const itemPathCandidates = candidates.paths;
    const itemNameCandidates = candidates.names;
    const filePath = this.normalizePath(file.path || '');
    const fileName = this.normalizePath(file.name || file.path?.split('/').pop() || '');

    let bestScore = 0;

    for (const itemPath of itemPathCandidates) {
      if (itemPath && filePath) {
        if (itemPath === filePath) { bestScore = Math.max(bestScore, 120); }
        else if (itemPath.endsWith(filePath) || filePath.endsWith(itemPath)) { bestScore = Math.max(bestScore, 100); }
      }
      if (itemPath && fileName && (itemPath.endsWith(`/${fileName}`) || itemPath.endsWith(fileName))) {
        bestScore = Math.max(bestScore, 90);
      }
      if (itemPath && fileName && itemPath.includes(fileName)) {
        bestScore = Math.max(bestScore, 60);
      }
    }

    for (const itemName of itemNameCandidates) {
      if (itemName && fileName && itemName === fileName) {
        bestScore = Math.max(bestScore, 80);
      }
      if (itemName && filePath && filePath.endsWith(`/${itemName}`)) {
        bestScore = Math.max(bestScore, 70);
      }
    }

    return bestScore;
  }

  selectNextFile(): void { this.navigateFile(1); }
  selectPreviousFile(): void { this.navigateFile(-1); }

  private navigateFile(delta: 1 | -1): void {
    const list = this.visibleFiles;
    if (!list.length) { return; }
    const idx = this.selectedFile ? list.indexOf(this.selectedFile) : -1;
    const next = idx >= 0
      ? (idx + delta + list.length) % list.length
      : delta === 1 ? 0 : list.length - 1;
    this.switchFile(list[next]);
  }

  private switchFile(file: ReviewFile, forceReload = false): void {
    if (!file) { return; }
    const sameFile = this.isSameFile(file, this.selectedFile);
    this.selectedFile = file;
    // O po-combo, ao receber valor pelo model, passa a listar só a opção escolhida; sem
    // isto, depois de trocar pelas setas o combo abriria com um único arquivo.
    if (this.fileCombo) { this.fileCombo.removeInitialFilter = true; }
    this.selectedFileValue = this.findOptionValueForFile(file);
    this.selectAnswerForFile(file);
    if (sameFile && !forceReload) { return; }
    this.loading = true;
    if (this.isStandalone) {
      this.loadSource(file.path);
      return;
    }
    this.loadDiff(file.path);
  }

  loadDiff(filePath?: string): void {
    this.diffHasNoLineChanges = false;
    // O histórico sai junto com o diff, e não depois dele: a espera vira a da chamada mais
    // lenta, e não a soma. No TFVC a conta ainda precisa do from/to que o diff traz.
    const diffVersions = this.startChangesetFreshness(filePath || this.currentSourcePath);
    this.smartReviewService.getDiff(this.resourceType, this.resourceId, filePath).pipe(takeUntil(this.destroy$)).subscribe({
      next: (payload: DiffExportResponse) => {
        this.diffFromChangeset = payload.fromChangeset;
        this.diffToChangeset = payload.toChangeset;
        diffVersions?.next({ from: payload.fromChangeset, to: payload.toChangeset });

        const lang = detectLanguage(filePath ?? '');
        // extractFileDiff isola o arquivo correto em diffs multi-arquivo (igual VS Code)
        const fileDiff = filePath ? extractFileDiff(payload.diff, filePath) : payload.diff;
        this.diffPair = parseDiffToPair(fileDiff, lang);
        this.diffHasNoLineChanges = this.diffPair.stats.added === 0 && this.diffPair.stats.removed === 0;
        this.loading = false;
        this.cdr.markForCheck();
        this.consumeInitialScrollToTop();
        this.schedulePendingFindingScroll();
      },
      error: () => {
        this.notificationServiceHM.showToast('Erro ao carregar diff', 'error');
        this.resetChangesetFreshness();
        this.loading = false;
        this.cdr.markForCheck();
        this.consumeInitialScrollToTop();
        this.schedulePendingFindingScroll();
      }
    });
  }

  /**
   * Começa a dizer se a versão revisada ainda é a vigente, em paralelo com o diff.
   *
   * Git não depende do diff (a revisão vem da lista de arquivos) e resolve sozinho. No TFVC o
   * histórico já sai agora, mas a conta espera o from/to do diff: quem chama entrega por
   * aqui o que vier no header. Falha não é erro da tela — o selo só não aparece.
   */
  private startChangesetFreshness(path: string): ReplaySubject<{ from: number | null; to: number | null }> | null {
    this.resetChangesetFreshness();
    if (this.isStandalone) { return null; }
    if (this.isGitChange || this.isGitComparison) {
      this.loadRevisionFreshness(path);
      return null;
    }
    if (!path?.startsWith('$/')) { return null; }

    const versions = new ReplaySubject<{ from: number | null; to: number | null }>(1);
    this.changesetFreshness = CHECKING_FRESHNESS;
    this.changesetFreshnessSub = combineLatest([
      versions,
      this.tfvcService.getTfvcFileHistory(path, undefined, this.changesetFreshnessPageSize)
    ])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: ([{ from, to }, history]) => {
          this.changesetFreshness = buildChangesetFreshness(history.changesets, from, to, !!history.nextContinuationToken);
          this.cdr.markForCheck();
        },
        error: () => {
          this.changesetFreshness = null;
          this.cdr.markForCheck();
        }
      });
    return versions;
  }

  /**
   * O equivalente git: dois históricos do arquivo — na revisão revisada e na branch hoje —
   * e a posição da versão revisada no segundo. Ver `buildRevisionFreshness`.
   */
  private loadRevisionFreshness(path: string): void {
    const source = this.gitFreshnessSource;
    const revision = this.files.find(file => file.path === path)?.revision || source?.revision;
    if (!source || !path || !revision) { return; }

    const limit = this.changesetFreshnessPageSize;
    // Branch de origem apagada depois do merge dá 404: o código foi parar na branch padrão,
    // e é contra ela que a pergunta continua fazendo sentido.
    const today$ = this.gitService.listFileHistory(source.slug, path, source.branch, limit).pipe(
      catchError(error => source.branch
        ? this.gitService.listFileHistory(source.slug, path, '', limit)
        : throwError(() => error))
    );
    this.changesetFreshness = CHECKING_FRESHNESS;
    this.changesetFreshnessSub = forkJoin({
      atReview: this.gitService.listFileHistory(source.slug, path, revision, 1),
      today: today$
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ atReview, today }) => {
          this.changesetFreshness = buildRevisionFreshness(atReview, today, today.length >= limit);
          this.cdr.markForCheck();
        },
        error: () => {
          this.changesetFreshness = null;
          this.cdr.markForCheck();
        }
      });
  }

  private resolveGitFreshnessSource(meta: ReviewFilesMeta): { slug: string; revision: string; branch: string } | null {
    if (this.isPullRequest) {
      // PR aberto: contra a branch de origem, que é onde chega push novo. Fechado ou
      // mergeado: contra a branch padrão, para onde o código foi. O estado é o da hora do
      // review e pode estar velho — por isso a origem apagada também cai na padrão (ver
      // loadRevisionFreshness).
      const ref = parsePullResourceId(this.resourceId);
      if (!ref) { return null; }
      const branch = this.isPullClosed ? '' : (meta.sourceBranch ?? '');
      return { slug: ref.slug, revision: '', branch };
    }
    if (this.isGitChange) {
      const ref = parseCommitResourceId(this.resourceId);
      return ref ? { slug: ref.slug, revision: ref.revision, branch: '' } : null;
    }
    if (this.isGitComparison) {
      // Como no TFVC: a referência é o destino; sem ele (upload), a revisão comparada.
      const revision = meta.toRevision || meta.fromRevision;
      return meta.repositorySlug && revision ? { slug: meta.repositorySlug, revision, branch: '' } : null;
    }
    return null;
  }

  private resetChangesetFreshness(): void {
    this.changesetFreshnessSub?.unsubscribe();
    this.changesetFreshnessSub = undefined;
    this.changesetFreshness = null;
  }

  // ─── Standalone: carrega fonte ao invés de diff (igual VS Code openDiff standalone) ──
  loadSource(filePath: string): void {
    if (!filePath) {
      this.selectedAnswerItem = this.answerItems[0] ?? null;
      this.answer = this.selectedAnswerItem?.answer ?? null;
      this.standaloneSource = '';
      this.diffFromChangeset = null;
      this.diffToChangeset = null;
      this.loading = false;
      this.consumeInitialScrollToTop();
      this.schedulePendingFindingScroll();
      return;
    }
    this.smartReviewService.getSource(this.resourceType, this.resourceId, filePath).pipe(takeUntil(this.destroy$)).subscribe({
      next: sourceText => {
        this.standaloneSource = sourceText;
        this.sourceLanguage = detectLanguage(filePath);
        this.diffFromChangeset = null;
        this.diffToChangeset = null;
        this.loading = false;
        this.cdr.markForCheck();
        this.consumeInitialScrollToTop();
        this.schedulePendingFindingScroll();
      },
      error: () => {
        this.notificationServiceHM.showToast('Erro ao carregar fonte', 'error');
        this.loading = false;
        this.cdr.markForCheck();
        this.consumeInitialScrollToTop();
        this.schedulePendingFindingScroll();
      }
    });
  }

  private consumeInitialScrollToTop(): void {
    if (!this.pendingInitialScrollToTop) { return; }
    this.pendingInitialScrollToTop = false;
    if (this.pendingFindingLine != null) {
      this.scheduleScrollToFinding(this.pendingFindingLine);
      return;
    }
    this.scheduleScrollToTop();
  }

  private schedulePendingFindingScroll(): void {
    if (this.pendingFindingLine != null) {
      this.scheduleScrollToFinding(this.pendingFindingLine);
    }
  }

  private scheduleScrollToFinding(line: number): void {
    this.clearScrollToTopTimers();
    const delays = [0, 120, 300, 600, 1000];
    for (const delay of delays) {
      const timerId = setTimeout(() => {
        this.monacoViewerRef?.onAnnotationClicked(line);
        const target = this.pendingFindingId ? document.getElementById(`finding-${this.pendingFindingId}`) : null;
        target?.scrollIntoView({ block: 'center', inline: 'nearest' });
      }, delay);
      this.scrollToTopTimers.push(timerId);
    }
  }

  private scheduleScrollToTop(): void {
    this.clearScrollToTopTimers();
    const delays = [0, 120, 300, 600];
    for (const delay of delays) {
      const timerId = setTimeout(() => this.scrollPageToTop(), delay);
      this.scrollToTopTimers.push(timerId);
    }
  }

  private clearScrollToTopTimers(): void {
    for (const timerId of this.scrollToTopTimers) {
      clearTimeout(timerId);
    }
    this.scrollToTopTimers = [];
  }

  private scrollPageToTop(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') { return; }
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
    const containers = document.querySelectorAll<HTMLElement>('.po-page-content, .sr-page-shell, .sr-modal-shell');
    containers.forEach(container => { container.scrollTop = 0; });
  }

  onFileChange(selection: unknown): void {
    const selectedValue = this.resolveSelectionValue(selection);
    const file = this.resolveFileFromSelectedValue(selectedValue);
    if (!file) { return; }
    this.switchFile(file);
  }

  private resolveSelectionValue(selection: unknown): string {
    const candidate: unknown = (selection as { value?: unknown })?.value ?? selection;
    if (typeof candidate === 'string') { return candidate; }
    if (typeof candidate === 'number') { return String(candidate); }
    return this.selectedFileValue || '';
  }

  private resolveFileFromSelectedValue(selectedValue: string): ReviewFile | null {
    if (!selectedValue) { return this.selectedFile; }
    const mapped = this.fileByOptionValue.get(selectedValue);
    if (mapped) { return mapped; }

    // Fallback defensivo: alguns eventos podem devolver path/name ao invés do value.
    const normalized = this.normalizePath(selectedValue);
    return this.files.find(file => {
      const normalizedPath = this.normalizePath(file.path || '');
      const normalizedName = this.normalizePath(file.name || '');
      return normalized === normalizedPath || normalized === normalizedName;
    }) || null;
  }

  private findOptionValueForFile(file: ReviewFile): string {
    for (const [value, mappedFile] of this.fileByOptionValue.entries()) {
      if (this.isSameFile(file, mappedFile)) {
        return value;
      }
    }
    return '';
  }

  setDiffMode(inline: boolean): void {
    this.monacoViewerRef?.setDiffMode(inline);
  }

  nextDiff(): void {
    this.monacoViewerRef?.nextDiff();
  }

  prevDiff(): void {
    this.monacoViewerRef?.prevDiff();
  }

  @HostListener('window:keydown', ['$event'])
  handleGlobalShortcuts(event: KeyboardEvent): void {
    const key = event.key;
    if (key === 'F7') {
      event.preventDefault();
      if (event.shiftKey) {
        this.prevDiff();
      } else {
        this.nextDiff();
      }
      return;
    }

    const target = event.target as HTMLElement | null;
    const tag = target?.tagName?.toUpperCase() ?? '';
    const isTypingField = !!target && (target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT');
    if (isTypingField || !event.altKey) { return; }

    if (key === 'ArrowDown') {
      event.preventDefault();
      this.selectNextFile();
      return;
    }

    if (key === 'ArrowUp') {
      event.preventDefault();
      this.selectPreviousFile();
      return;
    }

    if (this.isStandalone) { return; }

    if (key === '1') {
      event.preventDefault();
      this.setDiffMode(false);
      return;
    }

    if (key === '2') {
      event.preventDefault();
      this.setDiffMode(true);
      return;
    }

    if (key === '3') {
      event.preventDefault();
      this.setAnnotationsPanelCollapsed(false);
    }
  }

  /**
   * Copia o entendimento técnico em formato texto para a área de transferência
   */
  copyTechnicalDocAsText(): void {
    if (!this.technicalDocAnswer) {
      this.notificationServiceHM.showToast('Nenhum entendimento técnico disponível para copiar', 'warning');
      return;
    }

    const textContent = formatTechnicalDocAsText(this.technicalDocAnswer);
    
    navigator.clipboard.writeText(textContent).then(() => {
      this.notificationServiceHM.showToast('Entendimento técnico copiado para a área de transferência', 'success');
      this.cdr.markForCheck();
    }).catch(() => {
      this.notificationServiceHM.showToast('Erro ao copiar para a área de transferência', 'error');
      this.cdr.markForCheck();
    });
  }

  private updateDeleteReviewModalActions(): void {
    this.deleteReviewPrimaryAction = {
      ...this.deleteReviewPrimaryAction,
      label: this.deleteReviewLoading ? this.deleteActionLoadingLabel : this.deleteActionLabel,
      loading: this.deleteReviewLoading,
      disabled: this.deleteReviewLoading || !this.isReviewTerminalForDelete()
    };

    this.deleteReviewSecondaryAction = {
      ...this.deleteReviewSecondaryAction,
      disabled: this.deleteReviewLoading
    };
  }

}
